import { useContext, useRef, useState, useEffect, useMemo } from 'react';
import './login.css';
import Footer from '../../components/footer/Footer';
import { useConfettiText } from '../../hooks/useConfettiText';
import { loginCall } from '../../apiCalls';
import { parseRadioInvitation } from '../../utils/radioParticipationUrl';
import { AuthContext } from '../../context/AuthContext';
import { CircularProgress } from '@material-ui/core';
import { Link } from 'react-router-dom';
import { useMediaQuery } from 'react-responsive';
import SpotifyIframeNormalBlack from '../../utils/spotifyIframe';
import { ViewPortContext } from '../../context/ViewPortContext';
import { useLocation, useHistory } from 'react-router-dom';
import { useTheme } from '../../context/ThemeContext';
import GenericHelmet from '../../components/genericHelmet/GenericHelmet'; // import your generic helmet
import TrafficConstellationsCanvas from '../../components/traffic/TrafficConstellationsCanvas';
import { socket, getLatencyMs } from '../../context/socket';
import {
  clearStoredAuthReturnTo,
  firstSafeAuthReturnTo,
  getSafeAuthReturnToFromSearch,
  getSupportedMagicReturnTo,
  isSafeAuthReturnTo,
  sanitizeStoredAuthReturnTo,
} from '../../utils/authReturnTo';
import {
  getGoogleOAuthStartUrl,
  isGoogleOAuthEnabled,
} from '../../utils/googleOAuth';
import GoogleOAuthMark from '../../components/auth/GoogleOAuthMark';

const LOGIN_PARTICLE_COUNT = 100;
const AUTH_BASE = process.env.REACT_APP_AUTH_BASE_URL || '';
const MAGIC_LINK_SUCCESS_COPY =
  'Magic link sent. Open your email and tap the secure link to continue. It can take a few minutes to arrive. If you do not see it, check Junk/Spam.';
const MAGIC_LINK_SUCCESS_DETAIL =
  'Open your email and tap the secure link to continue. It can take a few minutes to arrive. If you do not see it, check Junk/Spam.';
const MAGIC_LINK_HONEYPOT_STYLE = {
  position: 'absolute',
  left: '-10000px',
  width: 1,
  height: 1,
  opacity: 0,
  pointerEvents: 'none',
};

const getPathname = (value) => {
  if (!isSafeAuthReturnTo(value)) return '';
  try {
    return new URL(value, 'https://www.joegalaxy.net').pathname;
  } catch (err) {
    return '';
  }
};

const hasLocalOpsMapIntent = (value) => {
  if (!isSafeAuthReturnTo(value)) return false;
  try {
    const parsed = new URL(value, 'https://www.joegalaxy.net');
    if (parsed.pathname !== '/playermap') return false;
    return [
      'intent',
      'type',
      'regionSlug',
      'rideTemplate',
      'draft',
      'firestarter',
      'firestarterCreate',
    ].some((key) => parsed.searchParams.has(key));
  } catch (err) {
    return false;
  }
};

const isLocalOpsTarget = (value) => {
  const pathname = getPathname(value);
  return (
    pathname === '/localops/continue' ||
    pathname.startsWith('/share/task/') ||
    pathname.startsWith('/share/offering/') ||
    pathname.startsWith('/tasks/') ||
    pathname.startsWith('/offerings/') ||
    hasLocalOpsMapIntent(value)
  );
};

const isPresenceTarget = (value) => getPathname(value) === '/presence-demo';

const isHitLabTarget = (value) => getPathname(value) === '/hitlab/intake';

const validateEmail = (value) => {
  const trimmed = String(value || '').trim();
  if (!trimmed) return 'Email is required.';
  const re = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;
  if (!re.test(trimmed)) return 'Enter a valid email address.';
  return '';
};

const upgradeSocketToAuthed = (token) => {
  if (!token || !socket) return;
  socket.auth = socket.auth || {};
  socket.auth.token = token;
  if (socket.auth.guest) delete socket.auth.guest;
  try {
    socket.emit('presence:heartbeat', { activityType: 'active', authed: true });
  } catch (err) {
    // ignore
  }
};

export default function Login() {
  const [loginSuccess, setLoginSuccess] = useState(false);

  const emailAddress = useRef();
  const password = useRef();
  const { isFetching, dispatch, player: authedPlayer } = useContext(AuthContext);
  const { height } = useContext(ViewPortContext);
  // console.log(height);
  //const { error } = useContext(AuthContext);
  const [error, setError] = useState('');
  const inputRef = useRef(null);
  const history = useHistory();
  const [logoUrl, setLogoUrl] = useState('');
  const [logoTagline, setLogoTagline] = useState('');
  const location = useLocation();
  const query = useMemo(
    () => new URLSearchParams(location.search),
    [location.search],
  );
  const returnToParam = query.get('returnTo');
  const redirectParam = query.get('redirect');
  const fromParam = query.get('from');
  const intentParam = query.get('intent');
  const safeQueryTarget = useMemo(
    () => getSafeAuthReturnToFromSearch(location.search),
    [location.search],
  );
  const [safeStoredTarget, setSafeStoredTarget] = useState(null);
  const safeContinuationTarget = safeQueryTarget || safeStoredTarget;
  const from = firstSafeAuthReturnTo(fromParam, redirectParam, returnToParam);
  const fromRef = useRef(null);
  const { darkTheme } = useTheme();
  const localOpsRedirectLabel = safeContinuationTarget?.includes('/tasks/')
    ? 'task'
    : safeContinuationTarget?.includes('/offerings/')
    ? 'offering'
    : 'item';
  const registerHref = safeContinuationTarget
    ? `/register/1?returnTo=${encodeURIComponent(safeContinuationTarget)}`
    : '/register/1';
  const loginContext = isHitLabTarget(safeContinuationTarget)
    ? 'hitlab'
    : isLocalOpsTarget(safeContinuationTarget)
    ? 'localops'
    : intentParam === 'presence-demo' || isPresenceTarget(safeContinuationTarget)
    ? 'presence'
    : 'default';
  const isLocalOpsLogin = loginContext === 'localops';
  const isHitLabLogin = loginContext === 'hitlab';
  const magicReturnTo = getSupportedMagicReturnTo(
    safeContinuationTarget,
    isLocalOpsLogin ? '/playermap' : '/playermap',
  );
  const googleOAuthHref = isGoogleOAuthEnabled()
    ? getGoogleOAuthStartUrl({
        authBaseUrl: AUTH_BASE,
        returnTo: magicReturnTo,
      })
    : '';
  const isGooglePrimary = isHitLabLogin && !!googleOAuthHref;
  const isMagicPrimary = isHitLabLogin && !googleOAuthHref;
  const isPasswordPrimary = !isHitLabLogin && !isMagicPrimary;

  // console.log('location.pathname: ', location.pathname);
  const desktopView = useMediaQuery({
    query: '(min-width: 805px)',
  });
  const mobileView = useMediaQuery({
    query: '(max-width: 805px)',
  });
  const geoPatchRef = useRef({});

  const [showConfettiText, ConfettiTextComponent] = useConfettiText(
    inputRef,
    mobileView,
    '+2 JGK Welcome!',
    LOGIN_PARTICLE_COUNT, // Particle count for the confetti
    'login', // Parent component name
    false, // isVoiceMemo
  );
  const [showPassword, setShowPassword] = useState(false); // toggle for password visibility
  const [magicEmail, setMagicEmail] = useState('');
  const [magicWebsite, setMagicWebsite] = useState('');
  const [magicEmailError, setMagicEmailError] = useState('');
  const [magicStatus, setMagicStatus] = useState('idle');
  const [magicMessage, setMagicMessage] = useState('');
  const magicRequestPendingRef = useRef(false);

  const isMounted = useRef(true);
  useEffect(() => {
    const storedTarget = sanitizeStoredAuthReturnTo(localStorage);
    setSafeStoredTarget(storedTarget);
    if (safeQueryTarget) {
      try {
        localStorage.setItem('jg:returnTo', safeQueryTarget);
      } catch (err) {
        // ignore storage failures
      }
    }
  }, [safeQueryTarget]);

  useEffect(() => {
    if (!loginSuccess && !authedPlayer?.token) return;
    const token = authedPlayer?.token;
    if (token) upgradeSocketToAuthed(token);
    const resume = localStorage.getItem('jg:resume');
    if (resume && !parseRadioInvitation(from)?.capsule) {
      clearStoredAuthReturnTo(localStorage);
      history.replace('/playermap');
      return;
    }
    const storedTarget = sanitizeStoredAuthReturnTo(localStorage);
    const rt = firstSafeAuthReturnTo(localStorage.getItem('jg:returnTo'));
    const legacyTarget = firstSafeAuthReturnTo(from, storedTarget);
    const target = rt || legacyTarget;
    if (target) {
      clearStoredAuthReturnTo(localStorage);
      history.replace(target);
    }
  }, [loginSuccess, authedPlayer?.token, history, from]);

  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  //console.log(desktopView);
  useEffect(() => {
    // If already authenticated, skip guest wiring
    if (authedPlayer?.token) return undefined;

    let heartbeatId = null;
    let recovering = false;

    const params = new URLSearchParams(window.location.search);
    const pickParam = (key) => params.get(key) || null;
    const locale = navigator.language || '';
    const localeRegion =
      (locale.includes('-') && locale.split('-')[1]) ||
      (locale.includes('_') && locale.split('_')[1]) ||
      null;

    const guestAuth = {
      guest: 'true',
      utm_source: pickParam('utm_source'),
      utm_medium: pickParam('utm_medium'),
      utm_campaign: pickParam('utm_campaign'),
      utm_content: pickParam('utm_content'),
      region: pickParam('region') || localeRegion,
      referrer: document.referrer || null, // avoid self-URL fallback to reduce self-attribution
    };

    Object.keys(guestAuth).forEach((k) => {
      if (guestAuth[k] === null || guestAuth[k] === '') delete guestAuth[k];
    });

    // Persist attribution so reconnects and post-login sessions carry source data.
    const attribForStorage = { ...guestAuth };
    delete attribForStorage.guest;
    try {
      localStorage.setItem('jg_guest_attrib', JSON.stringify(attribForStorage));
    } catch (e) {
      // ignore storage errors
    }

    // Force guest mode: strip any lingering auth/token before connecting
    if (socket.auth) {
      delete socket.auth.token;
    } else {
      socket.auth = {};
    }
    const prevAuth = socket.auth || {};
    socket.auth = { ...prevAuth, ...guestAuth };
    if (!socket.connected) socket.connect();

    const emitHeartbeat = (activityType = 'active') => {
      const sid = socket.id;
      if (!sid || !socket.connected) return;
      const latencyMs = getLatencyMs?.();
      socket.emit('presence:heartbeat', {
        guest: true,
        sessionId: sid,
        activityType,
        latencyMs: Number.isFinite(latencyMs) ? Math.round(latencyMs) : null,
        ...geoPatchRef.current,
      });
    };

    const startHeartbeat = () => {
      if (heartbeatId) return;
      heartbeatId = setInterval(() => {
        if (socket.connected) {
          const vis =
            document.visibilityState === 'visible' ? 'active' : 'background';
          emitHeartbeat(vis);
        }
      }, 10_000);
      const vis =
        document.visibilityState === 'visible' ? 'active' : 'background';
      emitHeartbeat(vis);
    };

    const stopHeartbeat = () => {
      if (heartbeatId) {
        clearInterval(heartbeatId);
        heartbeatId = null;
      }
    };

    const handleVisibility = () => {
      const vis =
        document.visibilityState === 'visible' ? 'active' : 'background';
      emitHeartbeat(vis);
    };
    const handleFocus = () => emitHeartbeat('active');
    const handleBlur = () => emitHeartbeat('background');
    const handleConnect = () => startHeartbeat();
    const handleDisconnect = () => stopHeartbeat();
    const handleClose = () => stopHeartbeat();
    const handleGuestError = (err) => {
      if (err?.message && err.message.includes('unknown session')) {
        stopHeartbeat();
        if (recovering) return;
        recovering = true;
        socket.disconnect();
        setTimeout(() => {
          socket.connect();
          recovering = false;
        }, 200);
      }
    };

    document.addEventListener('visibilitychange', handleVisibility);
    window.addEventListener('focus', handleFocus);
    window.addEventListener('blur', handleBlur);
    window.addEventListener('beforeunload', stopHeartbeat);
    socket.on('connect', handleConnect);
    socket.on('disconnect', handleDisconnect);
    socket.on('guest:error', handleGuestError);
    socket.on('close', handleClose);

    handleVisibility(); // initial send
    if (socket.connected) startHeartbeat();

    const requestGeoIfGranted = () => {
      if (!navigator?.permissions || !navigator?.geolocation) return;
      navigator.permissions
        .query({ name: 'geolocation' })
        .then((status) => {
          if (status.state !== 'granted') return;
          navigator.geolocation.getCurrentPosition(
            (pos) => {
              const { latitude, longitude, accuracy } = pos.coords || {};
              if (
                typeof latitude === 'number' &&
                typeof longitude === 'number'
              ) {
                geoPatchRef.current = {
                  geo_lat: Number(latitude.toFixed(6)),
                  geo_lon: Number(longitude.toFixed(6)),
                  geo_accuracy: Math.round(accuracy || 0),
                };
                emitHeartbeat(
                  document.visibilityState === 'visible'
                    ? 'active'
                    : 'background',
                );
              }
            },
            () => {},
            {
              enableHighAccuracy: false,
              timeout: 4000,
              maximumAge: 60000,
            },
          );
        })
        .catch(() => {});
    };

    requestGeoIfGranted();

    return () => {
      stopHeartbeat();
      document.removeEventListener('visibilitychange', handleVisibility);
      window.removeEventListener('focus', handleFocus);
      window.removeEventListener('blur', handleBlur);
      window.removeEventListener('beforeunload', stopHeartbeat);
      socket.off('connect', handleConnect);
      socket.off('disconnect', handleDisconnect);
      socket.off('guest:error', handleGuestError);
      socket.off('close', handleClose);
      if (!socket.auth?.token) socket.auth = {};
    };
  }, []);

  const handleClick = async (e) => {
    e.preventDefault();

    try {
      const result = await loginCall(
        {
          emailAddress: emailAddress.current.value,
          password: password.current.value,
        },
        dispatch,
      );

      if (isMounted.current) {
        if (result === 'success') {
          setLoginSuccess(true);
        } else {
          setError(
            result.response ? result.response.statusText : 'An error occurred',
          );
        }
      }
    } catch (err) {
      if (isMounted.current) {
        setError(err.response ? err.response.statusText : 'An error occurred');
      }
    }
  };

  // useEffect(() => {
  //   if (loginSuccess) {
  //     // console.log('loginSuccess');
  //     showConfettiText();
  //   }
  // }, [loginSuccess]);

  useEffect(() => {
    if (from) {
      localStorage.setItem('from', from);
    }

    const unlockedGalaxies =
      localStorage.getItem('unlockedGalaxies') === 'true';
    if (!unlockedGalaxies) {
      const originFrom = localStorage.getItem('originFrom');
      const safeOriginFrom = firstSafeAuthReturnTo(originFrom);
      if (!safeOriginFrom && from) {
        localStorage.setItem('originFrom', from);
      }
    }

    const originFrom = localStorage.getItem('originFrom');
    const safeOriginFrom = firstSafeAuthReturnTo(originFrom);
    if (from) {
      fromRef.current = from;
    } else if (safeOriginFrom) {
      fromRef.current = safeOriginFrom;
    }
  }, [from]);

  useEffect(() => {
    if (
      from?.includes('/galaxies/camden-live-galaxy') ||
      from?.includes('/galaxies/65e95c2b95ff6915e2f5f361')
    ) {
      // console.log('from galaxies/65e95c2b95ff6915e2f5f361');
      setLogoUrl(
        'https://res.cloudinary.com/joegalaxy/image/upload/v1715229523/thielxhez0teywzmwycg_srujsi.jpg',
      );
      setLogoTagline('Come on down, come on down!');
    } else if (
      from?.includes('/galaxies/la-joya-eco-community') ||
      from?.includes('667de222b7513bdf52193144')
    ) {
      setLogoUrl(
        'https://res.cloudinary.com/joegalaxy/image/upload/v1719531368/LaJoyaLogo_cbapwt.jpg',
      );
      setLogoTagline('Adventure, Philanthropy & Prosperity are a Way of Life.');
    } else if (from?.includes('/galaxies/660a1c8149a3f75c60b457e1')) {
      setLogoUrl(
        'https://res.cloudinary.com/joegalaxy/image/upload/q_auto/fl_progressive:steep/v1721518015/Images/tomq4dq5uiglbr2tcgil.png',
      );
      setLogoTagline('JGSoccer.Com Galaxy');
    } else if (
      from?.includes('/galaxies/66ef5e0d1db6c7febd8a5194') ||
      from?.includes('/galaxies/kebell-football')
    ) {
      setLogoUrl(
        'https://res.cloudinary.com/joegalaxy/image/upload/v1726985209/WhatsApp_Image_2024-09-22_at_00.57.28_f8e36b89_x9ufdf.jpg',
      );
      setLogoTagline('Kebell Football Galaxy');
    } else {
      setLogoUrl(
        'https://res.cloudinary.com/joegalaxy/image/upload/q_auto/v1658172769/Images/sbcgiu6mxdtvqvjflfwu.png',
      );
      if (loginContext === 'hitlab') {
        setLogoTagline('JG HitLab Artist Intake.');
      } else if (loginContext === 'localops') {
        setLogoTagline(
          'Continue your JG LocalOps action. Use the same email to return to your ride request, task, or local handoff.',
        );
      } else if (loginContext === 'presence') {
        setLogoTagline(
          'Step into live presence. See who is active, connected, and live in the JG Universe.',
        );
      } else {
        setLogoTagline(
          'Enter the JG Universe. Connect, contribute, and build your verified local legacy.',
        );
      }
    }
  }, [from, loginContext]);

  const handleMagicLink = async (event) => {
    event?.preventDefault?.();
    if (magicStatus === 'sending' || magicRequestPendingRef.current) return;
    const validation = validateEmail(magicEmail);
    setMagicEmailError(validation);
    if (validation) return;

    magicRequestPendingRef.current = true;
    setMagicStatus('sending');
    setMagicMessage('');
    try {
      const magicUrl = AUTH_BASE ? `${AUTH_BASE}/auth/magic` : '/auth/magic';
      const res = await fetch(magicUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          email: magicEmail.trim(),
          returnTo: magicReturnTo,
          website: magicWebsite,
        }),
      });
      if (!res.ok) throw new Error('magic-link-failed');
      setMagicStatus('sent');
      setMagicMessage(MAGIC_LINK_SUCCESS_COPY);
      setMagicEmail(magicEmail.trim());
    } catch (err) {
      setMagicStatus('error');
      setMagicMessage('Could not send magic link. Please try again.');
    } finally {
      magicRequestPendingRef.current = false;
    }
  };

  const getResetLink = () => {
    if (emailAddress.current) {
      const emailValue = emailAddress.current.value;
      return `/reset?email=${encodeURIComponent(emailValue)}`;
    }
    return '/reset';
  };

  const magicCard = (
    <div
      className={`login-auth-card loginMagicCard ${
        isMagicPrimary
          ? 'login-auth-card-primary loginMagicCardPrimary'
          : 'login-auth-card-secondary loginMagicCardSecondary'
      }`}
      data-testid="login-magic-card"
    >
      <div
        data-testid={
          isMagicPrimary ? 'login-magic-primary' : 'login-magic-secondary'
        }
      >
        <form onSubmit={handleMagicLink} noValidate>
          <div className="login-auth-card-title loginMagicTitle">
            Continue with Email
          </div>
          <div className="login-auth-card-copy loginMagicCopy">
            {isHitLabLogin
              ? "No password needed. We'll send a secure link back to HitLab."
              : "No password needed. We'll send a secure link that brings you back."}
          </div>
          {isLocalOpsLogin ? (
            <div className="loginMagicHelper">
              Best for ride requests, tasks, and local handoffs.
            </div>
          ) : null}
          <label className="loginMagicLabel" htmlFor="login-magic-email">
            Email address
          </label>
          <input
            id="login-magic-email"
            name="email"
            data-testid="login-magic-email"
            className="loginInput login-auth-input loginMagicInput"
            type="email"
            value={magicEmail}
            onChange={(e) => {
              setMagicEmail(e.target.value);
              if (magicEmailError) setMagicEmailError('');
            }}
            placeholder="you@example.com"
            autoComplete="email"
            inputMode="email"
            autoCapitalize="none"
            autoCorrect="off"
          />
          <input
            type="text"
            name="website"
            value={magicWebsite}
            onChange={(e) => setMagicWebsite(e.target.value)}
            autoComplete="off"
            tabIndex={-1}
            aria-hidden="true"
            data-testid="login-magic-website"
            style={MAGIC_LINK_HONEYPOT_STYLE}
          />
          {magicEmailError ? (
            <div className="error_text loginMagicError">{magicEmailError}</div>
          ) : null}
          <button
            type="submit"
            className="login-auth-submit loginMagicButton"
            data-testid="login-magic-submit"
            disabled={magicStatus === 'sending'}
          >
            {magicStatus === 'sending' ? 'Sending...' : 'Send magic link'}
          </button>
        </form>
        {magicMessage ? (
          <div
            role={magicStatus === 'sent' ? 'status' : 'alert'}
            className={
              magicStatus === 'sent'
                ? 'loginMagicStatus loginMagicStatusSuccess'
                : 'loginMagicStatus loginMagicStatusError'
            }
          >
            {magicStatus === 'sent' ? (
              <>
                <strong>Magic link sent.</strong>
                <span>{MAGIC_LINK_SUCCESS_DETAIL}</span>
              </>
            ) : (
              magicMessage
            )}
          </div>
        ) : null}
      </div>
    </div>
  );
  const googleOAuthCard = googleOAuthHref ? (
    <div
      className={`${
        darkTheme ? 'dark-theme ' : ''
      }login-auth-card ${
        isGooglePrimary
          ? 'login-auth-card-primary loginGoogleCardPrimary'
          : 'login-auth-card-secondary'
      } loginGoogleCard`}
      data-testid="login-google-oauth-card"
    >
      <div>
        <div className="login-auth-card-title loginGoogleTitle">
          <GoogleOAuthMark className="loginGoogleIcon" />
          <span>Continue with Google</span>
        </div>
        <div className="login-auth-card-copy">
          {isHitLabLogin
            ? "Secure Google sign-in. You'll return to HitLab."
            : "Secure Google sign-in. We'll bring you back."}
        </div>
      </div>
      <a
        href={googleOAuthHref}
        className="login-auth-submit loginGoogleButton"
        data-testid="login-google-oauth-link"
      >
        <GoogleOAuthMark className="loginGoogleButtonIcon" />
        <span>Continue with Google</span>
      </a>
    </div>
  ) : null;

  const passwordCard = (
    <form
      className={`${
        darkTheme ? 'dark-theme ' : ''
      }loginBox login-auth-card loginPasswordCard ${
        isPasswordPrimary ? 'login-auth-card-primary' : 'login-auth-card-secondary'
      }`}
      onSubmit={handleClick}
      data-testid="login-password-form"
    >
      <div>
        <div className="login-auth-card-title">
          Log in with password
        </div>
        <div className="login-auth-card-copy">
          Use your existing Joe Galaxy account password.
        </div>
      </div>
      <input
        placeholder="Email"
        type="email"
        required
        className="loginInput login-auth-input"
        ref={emailAddress}
        autoComplete="email"
      />
      <input
        placeholder="Password"
        type={showPassword ? 'text' : 'password'}
        required
        minLength="8"
        className="loginInput login-auth-input"
        ref={password}
        autoComplete="current-password"
      />
      <button
        type="button"
        className="togglePasswordBtn login-auth-inline-control"
        onClick={() => setShowPassword((prev) => !prev)}
        aria-label={showPassword ? 'Hide password' : 'Show password'}
      >
        {showPassword ? 'Hide password' : 'Show password'}
      </button>
      {/* {loginSuccess && <ConfettiTextComponent />} */}

      {error && <div className="error_text">{error}</div>}
      <button
        ref={inputRef}
        className="loginButton login-auth-submit"
        type="submit"
        disabled={isFetching}
        data-testid="login-password-primary"
      >
        {isFetching ? (
          <CircularProgress color="inherit" size="20px" />
        ) : (
          'Log In'
        )}
      </button>
      <Link
        to={getResetLink()}
        className="forgotPassword"
        style={{ textDecoration: 'none' }}
        title="Joe Galaxy Password Recovery"
      >
        <span className="loginForgot">Forgot Password?</span>
      </Link>
      <div className="loginCenter">
        <Link
          to={registerHref}
          style={{ textDecoration: 'none' }}
          title="Create a New Joe Galaxy Player Account"
          data-testid="login-register-link"
        >
          <button
            className="loginRegisterButton"
            disabled={isFetching}
          >
            Create a New Account
          </button>
        </Link>
      </div>
    </form>
  );

  // console.log(from);
  const loginClassName = [
    darkTheme ? 'dark-theme blackBg login' : 'login',
    isHitLabLogin ? 'login-hitlab-context' : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <>
      <GenericHelmet
        title="Login | JOE GALAXY.NET"
        description="Log in to JOEGALAXY.NET to access your adventure profile, join group chats, and continue exploring our interactive, gamified social platform."
        image="https://res.cloudinary.com/joegalaxy/image/upload/q_auto/v1664565843/Images/JOEGALAXYDopeGalaxyLogooriginal_tecmer.png"
        url="https://www.joegalaxy.net/login"
        regionName="Worldwide"
      />
      <TrafficConstellationsCanvas
        counterClassName="login-galaxy-gate-counter"
        counterTestId="login-galaxy-gate-counter"
      />
      <div className={loginClassName}>
        <div className="loginWrapper">
          <div className="loginLeft">
            <div>
              <img src={logoUrl} alt="Joe Galaxy Logo"></img>
            </div>
            {/* <h3 className="loginLogo">JOE GALAXY</h3> */}
            <span className="loginDesc">
              <p>
                {logoTagline.split(/\.\s|!\s/)[0] +
                  (logoTagline.includes('.')
                    ? '.'
                    : logoTagline.includes('!')
                    ? '!'
                    : '')}
              </p>
              <p className="tag">
                {logoTagline
                  .split(/\.\s|!\s/)
                  .slice(1)
                  .join('. ')}
              </p>
            </span>
            {/* <SpotifyIframeNormalBlack /> */}
          </div>
          <div className="loginRight">
            <div className="logoDescMobile" data-testid="login-mobile-hero">
              <div>
                <img src={logoUrl} alt="Joe Galaxy Logo"></img>
              </div>
              {/* <h3 className="loginLogo">JOE GALAXY</h3> */}
              <span className="loginDesc">
                <p>
                  {logoTagline.split(/\.\s|!\s/)[0] +
                    (logoTagline.includes('.')
                      ? '.'
                      : logoTagline.includes('!')
                      ? '!'
                      : '')}
                </p>
                <p className="tag">
                  {logoTagline
                    .split(/\.\s|!\s/)
                    .slice(1)
                    .join('. ')}
                </p>
              </span>
            </div>
            {isLocalOpsLogin && (
              <div className="auth-redirect-banner">
                Finish signing in to view this LocalOps {localOpsRedirectLabel}.
              </div>
            )}
            {isHitLabLogin && (
              <div
                className="auth-redirect-banner hitlab-auth-bridge"
                data-testid="login-hitlab-bridge"
              >
                <span>JG HITLAB ARTIST INTAKE</span>
                <strong>Create Your JG HitLab Account</strong>
                <p>
                  One secure sign-in opens your artist intake and brings you
                  back to your artist profile.
                </p>
                <small>One account. One artist profile. One record.</small>
              </div>
            )}
            <div className="login-auth-stack">
              {isHitLabLogin ? (
                <>
                  {googleOAuthCard}
                  {magicCard}
                  {passwordCard}
                </>
              ) : (
                <>
                  {passwordCard}
                  {googleOAuthCard}
                  {magicCard}
                </>
              )}
            </div>
            {/* Subtle presence note for credibility without altering layout */}
            <div
              style={{
                fontSize: 12,
                color: darkTheme ? '#9aa5b1' : '#475467',
                textAlign: 'center',
                marginTop: 12,
              }}
            >
              {/* On mobile (no footer), link to Presence Engine landing for credibility */}
              {mobileView ? (
                <a
                  href="https://presence-sdk-landing.joegalaxy.net"
                  style={{ color: 'inherit', textDecoration: 'none' }}
                  aria-label="Powered by JG Presence Engine (live)"
                >
                  Powered by JG Presence Engine (live)
                </a>
              ) : (
                'Powered by JG Presence Engine (live)'
              )}
            </div>
          </div>
        </div>
      </div>
      {!mobileView && height >= 563 && <Footer />}
    </>
  );
}
